/**
 * Time warp: how fast the trip plays is the player's.
 *
 * One clock, and warp is its rate: `src/world/clock.ts` holds the levels and
 * the ramp, and this is the control, the automatic drop and the readout. The
 * debug sim-speed scrub that used to multiply `dt` here went with it - a
 * second multiplier standing on top of the clock's own rate was two clocks
 * wearing one label, and nothing downstream could tell which it was reading.
 *
 * Every drop lands inside one tick and resets the pick to real time, so the
 * player reaches for warp again once they are past whatever caused it.
 */

import { bandEdges } from '../atlas/camera.ts';
import type { InputHandle } from '../input.ts';
import { heatHoldsClock } from '../scene/heat.ts';
import type { Custos } from '../scene/custos.ts';
import { track } from '../visits/bus.ts';
import { gameState, setWarpLevel } from '../state/gameState.ts';
import { devFlag, urlFlag, TEST_SWITCHES } from '../urlFlags.ts';
import { bodyById } from '../world/bodies.ts';
import { clockRateText, gameClock, REAL_TIME_RATE, WARP_LEVELS } from '../world/clock.ts';
import type { WorldVec } from '../world/frame.ts';
import type { BodyRow } from '../world/types.ts';
import type { Autopilot, AutopilotCommand } from './autopilot.ts';
import type { BodyPlaces } from './bodies.ts';
import type { FlightAxes, FlightPull, FlightVec } from './flight.ts';
import type { PlayerVitalsHandle } from './playerVitals.ts';
import type { SimPositionHandle } from './position.ts';
import { nearerWarpDrop, type WarpDropCrossing } from './warpDrop.ts';

/** The dominant-pull body's descent envelope, and where the ship is against it. */
export interface WarpEnvelope {
  readonly id: string;
  readonly name: string;
  readonly bodyAt: { readonly x: number; readonly y: number; readonly z: number };
  /** `radius_m + envelopeM`: the drop's own sphere, measured from the centre. */
  readonly captureRadiusM: number;
  readonly altitudeM: number;
  readonly envelopeM: number;
  readonly inside: boolean;
  /** Metres a second the range to this body is shutting; negative while it opens. */
  readonly closingMps: number;
  /** Bound conic about this body with apoapsis inside the envelope, periapsis clear of the deck and 100 km, band clear of its moons. */
  readonly stableOrbit?: boolean;
}

/** Periapsis floor of a stable orbit, metres over the surface, where the periapsis clears no deck or corridor top. */
const WARP_STABLE_PERIAPSIS_MIN_M = 100_000;
const WARP_MOON_CLEARANCE_RADII = 10.2;

/**
 * `?warpstick=1` leaves the control and the ramp live and suppresses the DROPS
 * alone: the broken-on-purpose variant `tools/preview/descent-dojo.mjs --warp
 * --inject warp-stick` opens, where a rate held through a descent-envelope
 * crossing must fail. Nothing a player reaches sets it.
 */
export const warpStick = devFlag(() => TEST_SWITCHES && urlFlag('warpstick'));
/**
 * `?warpanyaxis=1` restores the pre-fix gate, where turning the ship alone
 * (no thrust) counted as "under control" and took the level away: the broken-on-
 * purpose variant `tools/preview/descent-dojo.mjs --warp --inject
 * warp-any-axis` opens. Nothing a player reaches sets it.
 */
export const warpAnyAxis = devFlag(() => TEST_SWITCHES && urlFlag('warpanyaxis'));
/**
 * `?warpstalerate=1` puts the pre-fix ORDER back on purpose: `tick()`'s own
 * `dt` is derived from the rate the clock held BEFORE `updateWarp()` ran that
 * same frame, rather than after, so a frame whose thrust drops the clock
 * still spends that frame's delta-v at the warp rate it is about to leave.
 * The broken-on-purpose variant `--warp --inject warp-stale-rate` opens this.
 * Nothing a player reaches sets it.
 */
export const warpStaleRate = devFlag(() => TEST_SWITCHES && urlFlag('warpstalerate'));
/**
 * `?warpthrustdrop=1` restores the pre-fix gate, where thrust at ANY level
 * dropped the clock all the way to real time: the broken-on-purpose variants
 * `tools/preview/descent-dojo.mjs --warp --inject warp-thrust-drop` and
 * `--reach --inject warp-thrust-drop` open it. Nothing a player reaches sets it.
 */
export const warpThrustDrop = devFlag(() => TEST_SWITCHES && urlFlag('warpthrustdrop'));
/**
 * `?viewwarpfrozen=1` restores the pre-fix skip, where `tick()`'s Solar System
 * branch never ran the warp policy at all, so held thrust left the picked
 * level standing while the map was open: the broken-on-purpose variant
 * `tools/preview/descent-dojo.mjs --viewwarp --inject viewwarp-frozen` opens
 * it. Nothing a player reaches sets it.
 */
export const viewWarpFrozen = devFlag(() => TEST_SWITCHES && urlFlag('viewwarpfrozen'));
/**
 * Envelope-widths out the approach ceiling engages, closing only: one width
 * of runway beyond the drop's own edge, so the final leg is a leg and not an
 * instant clamp at the envelope. `?warpapproachnoceiling=1` drops the rule -
 * `tools/preview/descent-dojo.mjs --warp --inject approach-noceiling` opens
 * it, the top level's own control. Nothing a player reaches sets it.
 */
export const WARP_APPROACH_FACTOR = 2;
export const warpApproachNoCeiling = devFlag(() => TEST_SWITCHES && urlFlag('warpapproachnoceiling'));
/** `?warpboundorbitoff=1` drops the stable-orbit exemption; `descent-dojo.mjs --warp --orbit --inject bound-orbit-off` opens it. */
export const warpBoundOrbitOff = devFlag(() => TEST_SWITCHES && urlFlag('warpboundorbitoff'));
/** `?warpperifloor0=1` zeroes the stable-orbit periapsis floor; `--warp --orbit --inject peri-floor-off` opens it. */
export const warpPeriFloorOff = devFlag(() => TEST_SWITCHES && urlFlag('warpperifloor0'));
/**
 * `?warpdroppoint=1` restores the pre-fix point test on the envelope drop -
 * reads only this frame's end position, not the segment travelled to reach
 * it. `--inject warp-drop-point` opens it. Nothing a player reaches sets it.
 */
export const warpDropPoint = devFlag(() => TEST_SWITCHES && urlFlag('warpdroppoint'));
/**
 * `?warpdropendfirst=1` restores the pre-fix ORDERING on the envelope drop -
 * a frame crossing two capture spheres names the end body's sweep whatever
 * the start body's read. `tools/preview/warp-drop-check.mjs` reads it.
 * Nothing a player reaches sets it.
 */
const warpDropEndFirst = devFlag(() => TEST_SWITCHES && urlFlag('warpdropendfirst'));
/**
 * `?warpsweephelio=1` restores the pre-fix FRAME on the envelope drop - the
 * segment is taken in heliocentric metres, so the dominant body's own travel
 * is inside it. `--inject sweep-helio` opens it. Nothing a player reaches sets it.
 */
export const warpSweepHelio = devFlag(() => TEST_SWITCHES && urlFlag('warpsweephelio'));
/** `?analyticsperframe=1`: an analytics call on every warp tick, `analytics-check.mjs --frames --inject per-frame`. */
const analyticsPerFrame = devFlag(() => TEST_SWITCHES && urlFlag('analyticsperframe'));
/** Real seconds the drop's own notice stays up. */
const WARP_NOTICE_S = 4;
/** Real milliseconds the rate readout measures the clock over: long enough to carry
 *  a WARP TO NEXT frame's jump without flicker, short enough to follow the ease. */
const RATE_WINDOW_MS = 250;
/** `?warpreadoutnominal=1`: the readout shows the rate the clock is set to rather
 *  than the one it moves at. autowarp-check's readout control. */
const warpReadoutNominal = devFlag(() => TEST_SWITCHES && urlFlag('warpreadoutnominal'));

/** GO's Auto-warp as `drive.ts` runs it: whether it has the clock, its line for the
 *  control, and standing it down for the rest of the trip. */
export interface AutoWarpHooks {
  driving(): boolean;
  line(): string | null;
  standDown(): void;
}

/** The line the control's face and the Atlas row both carry while it holds. */
export const HEAT_HOLD_LINE = 'HEAT HOLDS THE CLOCK AT 1X';
/** The same, while the player pushes the drive past the red line. */
export const RED_LINE_HOLD_LINE = 'PAST THE RED LINE - THE CLOCK HOLDS AT 1X';

export interface WarpDeps {
  input: InputHandle;
  ship: Custos;
  vitals: Pick<PlayerVitalsHandle, 'redLine' | 'heatStepInput'>;
  simPos: SimPositionHandle;
  BODY_WORLD_VELOCITY: FlightVec;
  AUTOPILOT_RATE_CANCELS: boolean;
  CLOCK_CONST: boolean;
  pickDominantPull: (at: FlightVec) => number;
  shipWorldAt: () => WorldVec;
  shipWorldVelocity: () => FlightVec;
  requestAutopilotWarp: (rate: number, ceiling: number) => void;
  notePilotInput: () => void;
  /** Live reads and one live write: main.ts still owns these. */
  readonly engagement: object | null;
  readonly autopilot: Autopilot | null;
  readonly autopilotCmd: AutopilotCommand | null;
  autopilotWarpWish: number | null;
  readonly _pulls: readonly FlightPull[];
  readonly _pullIds: readonly string[];
  readonly bodyRows: readonly BodyRow[];
  readonly simBodies: BodyPlaces | null;
  readonly flightSimSeconds: number;
  readonly heldCruiseThrottle: number;
}

/** An instant the clock is warped to and stopped at; the ladder, ceiling and every drop still apply. */
export interface WarpGoal {
  /** The control's standing line while the run is on. */
  readonly runningLine: string;
  /** The notice it lands with. */
  readonly arrivedLine: string;
  /** Simulated seconds from the clock to the goal as now predicted; null where it is lost. */
  remainingS(): number | null;
  /** Simulated seconds one frame may carry the clock past the goal and still land in it. */
  slackS(): number;
  reached(): boolean;
}

export interface WarpHandle {
  /** `?flightwarp=` and `simFlight.setWarp()` pin the rate; no drop takes it. */
  pinned: boolean;
  /** Which level the player picked; index into `WARP_LEVELS`. */
  readonly pickIdx: number;
  readonly noticeText: string | null;
  readonly noticeUntilS: number;
  /** What the last drop test read, for `--warp --park`'s own bar. */
  readonly sweepMinM: number | null;
  /** What the control's own face is showing right now, off the DOM it paints. */
  readonly readout: {
    readonly levelText: string | null;
    readonly noticeText: string | null;
  };
  /** A seed or a park leaves no segment for the next drop test to sweep. */
  invalidateSweep(): void;
  underControl(): boolean;
  envelope(): WarpEnvelope | null;
  ceilingIdx(): number;
  reachable(idx: number): boolean;
  reason(idx: number): string | null;
  heatHolds(): boolean;
  /** What the control says while the heat holds the clock, null while it does not. */
  heatHoldLine(): string | null;
  showNotice(text: string, capKey?: string | null): void;
  drop(reason: string): void;
  setPick(idx: number, pilot?: boolean): void;
  /** Is the outward park's plane coming off its own radial `r x v`? */
  readonly parkRadialBasis: boolean;
  /** Arm or disarm that control. A check's own switch; no player reaches it. */
  setParkRadialBasis(on: boolean): void;
  /** The clock instant the skip in hand is running to, or null while none is running. */
  readonly skipTargetT: number | null;
  /** Real milliseconds and ticks the skip in hand has spent. Read by the check. */
  readonly skipMs: number;
  readonly skipTicks: number;
  /** True while a skip is still running its ticks. */
  readonly skipping: boolean;
  /** Arm the skip: sets the target and zeroes the accumulated ms and ticks. */
  startSkip(targetT: number): void;
  /** End the skip in hand, if one is running. */
  clearSkip(): void;
  /** One tick spent: bump the tick count and add its real milliseconds. */
  addSkipTick(ms: number): void;
  /**
   * A load swapped the state under the pick; hand the clock the rate it names,
   * unless a pin is holding the rate, which outranks the load.
   */
  adoptPick(): void;
  /** Start a run toward `goal`; the reason it is refused, or null once it runs. */
  runTo(goal: WarpGoal): string | null;
  /** The autopilot's own pace, any rate up to the ceiling, landed as given. */
  setAutopilotRate(rate: number): void;
  /** Hand GO's Auto-warp to the control: its drops, its hold line and a pick by hand. */
  bindAutoWarp(hooks: AutoWarpHooks): void;
  readonly autoWarpDriving: boolean;
  /** Simulated seconds a real second the clock has really moved over the last
   *  quarter second, WARP TO NEXT's jumps included: what the readouts show. */
  readonly measuredRate: number;
  /** The run in hand, or null. A pick by hand, a drop, a load or a fight ends it. */
  readonly goal: WarpGoal | null;
  paint(): void;
  update(): void;
}

export function createWarp(deps: WarpDeps): WarpHandle {
  const {
    input, ship, vitals, simPos, BODY_WORLD_VELOCITY, AUTOPILOT_RATE_CANCELS, CLOCK_CONST,
    pickDominantPull, shipWorldAt, shipWorldVelocity, requestAutopilotWarp, notePilotInput,
  } = deps;

  /**
   * `?flightwarp=N` sets the clock's rate at boot and PINS it: the ramp is
   * skipped, the control is inert and no drop fires. Round 13's own flag, and
   * the arms that use it park inside Mars's descent envelope and hold the rate
   * for ten minutes of wall, where a player-facing drop would take the reading
   * away from them. `simFlight.setWarp()` pins the same way. Nothing a player
   * reaches sets either.
   */
  let warpPinned = false;

  /** Which level the player picked; index into `WARP_LEVELS`. Lives in the state. */
  function warpPick(): number { return gameState().world.warpPick; }
  let warpNoticeText: string | null = null;
  /** One letter inside that notice to draw as a key cap, or null. */
  let warpNoticeKey: string | null = null;
  let warpNoticeUntilS = 0;
  let warpPaintKey = '';
  let warpFightWas = false;
  /** The body the last envelope test read, and whether the ship was inside it. */
  let warpEnvelopeBody = '';
  let warpEnvelopeInside = false;

  /**
   * The control behind `orbit-rings.mjs --trajectory --inject park-radial-basis`:
   * take an outward park's plane from its own `r x v`, which is degenerate by
   * construction. It is a switch rather than a `?` flag because a run-wide one
   * takes down the crossing leg's own park before the leg that reads the frame
   * ever runs. Only `simFlight.setParkRadialBasis` moves it.
   */
  let parkRadialBasis = false;

  /** The clock instant the skip is running to, or null while none is running. */
  let warpSkipTargetT: number | null = null;
  /** Real milliseconds and ticks the skip in hand has spent. Read by the check. */
  let warpSkipMs = 0;
  let warpSkipTicks = 0;

  let warpGoal: WarpGoal | null = null;
  /** Real seconds between the last two `updateWarp()` calls, held at its recent peak:
   *  the frame the run picks a level for has not happened yet. */
  let warpFramePeakS = 0.1;
  let warpLastUpdateMs = 0;
  /** GO's Auto-warp, once `drive.ts` has bound it. */
  let autoWarp: AutoWarpHooks | null = null;
  const autoWarpDriving = (): boolean => autoWarp !== null && autoWarp.driving();
  /** The clock as each `updateWarp()` found it: real ms and clock seconds, newest last. */
  const rateSamplesMs: number[] = [];
  const rateSamplesT: number[] = [];
  let measuredRate = REAL_TIME_RATE;

  const elWarpBar = document.getElementById('warp-bar');
  const elWarpLevel = document.getElementById('sim-speed-val');
  const elWarpNotice = document.getElementById('warp-notice');

  /**
   * Is the player asking for thrust right now? Rotating the ship (pitch, yaw,
   * roll - keys or the right-drag, since `input.ts`'s `recompute()` feeds both
   * into the one `FlightAxes`) changes where it points, not how fast it is
   * going, so it does not count: only the throttle does. `?warpanyaxis=1`
   * restores the pre-fix reading, where any attitude axis counted too.
   */
  function warpUnderControl(): boolean {
    const held = gameState().ship.flight.held;
    const throttle = held
      ? held.throttle
      : (input.state.throttleKey ? input.state.throttle : deps.heldCruiseThrottle);
    if (throttle !== 0) return true;
    if (!warpAnyAxis()) return false;
    const axes: FlightAxes = held ?? input.state;
    return axes.pitch !== 0 || axes.yaw !== 0 || axes.roll !== 0;
  }

  /**
   * The envelope is `bandEdges(radius).SYSTEM`, the ORBIT band's outer edge -
   * the altitude LAND's own ladder opens at, derived from the body's radius the
   * one way the Atlas derives it. No number is invented here.
   *
   * The pull centres are the tick's own `_pulls`, placed at this frame's own
   * epoch, and the ship's place is `simPos`, which is this frame's too. Closing
   * speed is read the same way `guidanceToTarget` reads it against a picked
   * target - relative velocity dotted onto the line to the body - so a ship
   * matching the body's own orbit reads 0 and neither approaches nor recedes.
   */
  function warpEnvelope(): WarpEnvelope | null {
    const shipAt = shipWorldAt();
    const bestIdx = pickDominantPull(shipAt);
    if (bestIdx < 0) return null;
    const pull = deps._pulls[bestIdx]!;
    const bestDist = Math.hypot(pull.at.x - shipAt.x, pull.at.y - shipAt.y,
      pull.at.z - shipAt.z);
    const id = deps._pullIds[bestIdx] ?? '';
    const row = bodyById(deps.bodyRows, id);
    if (!row) return null;
    const altitudeM = Math.max(0, bestDist - row.radius_m);
    const envelopeM = bandEdges(row.radius_m).SYSTEM;
    const bv = deps.simBodies?.velocityAt(id) ?? BODY_WORLD_VELOCITY;
    const v = shipWorldVelocity();
    const ux = (pull.at.x - shipAt.x) / bestDist;
    const uy = (pull.at.y - shipAt.y) / bestDist;
    const uz = (pull.at.z - shipAt.z) / bestDist;
    const closingMps = (v.x - bv.x) * ux + (v.y - bv.y) * uy + (v.z - bv.z) * uz;
    const stableOrbit = isStableOrbit(row, id, pull.at, pull.gm, bestDist, envelopeM,
      (v.x - bv.x) ** 2 + (v.y - bv.y) ** 2 + (v.z - bv.z) ** 2,
      -closingMps * bestDist);
    return { id, name: row.display_name, bodyAt: { x: pull.at.x, y: pull.at.y, z: pull.at.z },
      captureRadiusM: row.radius_m + envelopeM, altitudeM, envelopeM,
      inside: altitudeM < envelopeM, closingMps, stableOrbit };
  }

  /** `rDotV` is r . v relative to the body; the moon test runs last, over the registry rows. */
  function isStableOrbit(
    row: BodyRow, id: string, at: FlightVec, gm: number, r: number, envelopeM: number,
    v2: number, rDotV: number,
  ): boolean {
    if (!(gm > 0) || !(r > 0)) return false;
    const energy = v2 / 2 - gm / r;
    if (!(energy < 0)) return false;
    const a = -gm / (2 * energy);
    const h2 = r * r * v2 - rDotV * rDotV;
    const e = Math.sqrt(Math.max(0, 1 - h2 / (gm * a)));
    const apoAltM = a * (1 + e) - row.radius_m;
    const periAltM = a * (1 - e) - row.radius_m;
    const periR = a * (1 - e);
    const apoR = a * (1 + e);
    if (!(apoAltM < envelopeM
      && periAltM > (warpPeriFloorOff() ? 0 : Math.max(atmosphereTopM(row), WARP_STABLE_PERIAPSIS_MIN_M)))) {
      return false;
    }
    const rows = deps.bodyRows;
    for (let k = 0; k < rows.length; k++) {
      const moon = rows[k]!;
      if (moon.parent !== id) continue;
      // A moon with no live pull (oneBody) is placed from the registry; unplaced fails safe.
      const m = pullCentre(moon.id) ?? deps.simBodies?.worldAt(moon.id) ?? null;
      if (m === null) return false;
      const dj = Math.hypot(m.x - at.x, m.y - at.y, m.z - at.z);
      const reach = WARP_MOON_CLEARANCE_RADII * moon.radius_m;
      if (periR < dj + reach && apoR > dj - reach) return false;
    }
    return true;
  }

  /** Highest altitude the body's air or cloud deck reaches, from the registry row; 0 with none. */
  function atmosphereTopM(row: BodyRow): number {
    const air = row.atmosphere;
    let top = 0;
    if (air.kind === 'data') top = Math.max(air.deck_top_m ?? 0, air.opaque_deck_m ?? 0);
    const c = row.corridor;
    if (c !== null && c.bands.length > 0) top = Math.max(top, c.deck_m + c.bands[c.bands.length - 1]!.top_m);
    return top;
  }

  /**
   * The approach rule's own reading: the dominant body's envelope, widened by
   * `WARP_APPROACH_FACTOR`, gated on actually closing. A flyby that is not
   * approaching - or a body merely nearby with the range opening - changes
   * nothing, which is what keeps ordinary cruise unrestricted everywhere but
   * the final leg. `?warpapproachnoceiling=1` is this rule's own control.
   */
  /**
   * Below this closing rate there is no rendezvous to protect, only integration
   * noise on a circular park. It does not cover an eccentric orbit, whose
   * inbound half closes at metres a second: a stable orbit is exempted by
   * `stableOrbit` instead.
   */
  const WARP_APPROACH_CLOSING_MIN_MPS = 1;

  function warpApproaching(): WarpEnvelope | null {
    if (warpApproachNoCeiling()) return null;
    const env = warpEnvelope();
    if (env === null) return null;
    if (!(env.altitudeM < env.envelopeM * WARP_APPROACH_FACTOR)) return null;
    if (!(env.closingMps > WARP_APPROACH_CLOSING_MIN_MPS)) return null;
    if (env.stableOrbit === true && !warpBoundOrbitOff()) return null;
    return env;
  }

  /** Index of the `1 HR/S` level: the highest the approach rule allows on its own. */
  const WARP_APPROACH_CEILING_IDX = WARP_LEVELS.findIndex((l) => l.label === '1 HR/S');

  /**
   * The highest level the player may hold right now.
   *
   * Thrust and warp coexist below the top one. `FLIGHT_MAX_SUBSTEPS` is sized so
   * a day a second still gets the 2 s substep above, so gravity is already
   * integrated finely enough there and thrust rides the same substeps; nothing
   * about the integrator asks for a drop. What the top level does need
   * protecting from is delta-v granularity - one frame of full throttle at
   * 86,400:1 is 4,236 m/s against about 0.05 m/s at real time - so it alone steps
   * down one, and every level under it is left alone.
   *
   * The approach rule asks the identical shape for a different reason: at 60 fps
   * and a body moving 29.8 km/s (Earth), the top level moves the target 43,000 km
   * a frame against a 58,600 km envelope - three quarters of it between two
   * frames, unclosable - while 1 HR/S moves it 3 percent, which a rendezvous can
   * fly down. Each rule is read as how far IT drops the ceiling from the top,
   * and this is the MAXIMUM of the two drops, not a second mechanism: whichever
   * rule is asking for more restriction wins, and the other never loosens it
   * back up once it is looser than the ceiling already standing.
   */
  function warpCeilingIdx(): number {
    const top = WARP_LEVELS.length - 1;
    const thrustDrop = !warpUnderControl() ? 0 : (warpThrustDrop() ? top : 1);
    // Auto-warp paces the approach off the plan's own count, never a frame past it.
    const approachDrop = autoWarpDriving() || warpApproaching() === null ? 0 : top - WARP_APPROACH_CEILING_IDX;
    return top - Math.max(thrustDrop, approachDrop);
  }

  /** Whether a level (an index into `WARP_LEVELS`) could be picked right now. */
  function warpReachable(idx: number): boolean {
    return !warpPinned && idx <= warpCeilingIdx();
  }

  /**
   * Why `warpReachable(idx)` reads false, or null when it is true. Thrust wins
   * the shared tie at the ceiling both rules land on today (each drops the top
   * level by one rung) since it is the one the player can release by letting go
   * of the throttle; the approach is not something releasing anything changes.
   * The ladder holds the clock rather than the pick, so above real time it is
   * the reason every level above it is held.
   */
  function warpReason(idx: number): string | null {
    if (warpPinned) return 'PINNED';
    if (heatHoldsWarp()) return idx > 0 ? (vitals.redLine ? 'RED LINE' : 'HEAT CEILING') : null;
    if (idx <= warpCeilingIdx()) return null;
    if (warpUnderControl()) return 'THRUST';
    const env = warpApproaching();
    return env === null ? null : `NEAR ${env.name.toUpperCase()}`;
  }

  /**
   * Closest approach of the segment `[a, b]` to the ORIGIN, which is where the
   * capture sphere sits in the frame this is read in. Reading only `b` is what
   * let a frame stepping clean through the envelope (its width, under one
   * frame's travel) never trip the drop; `?warpdroppoint=1` restores that point
   * test.
   */
  function sweptMinDistance(
    ax: number, ay: number, az: number, bx: number, by: number, bz: number,
  ): number {
    const dx = bx - ax;
    const dy = by - ay;
    const dz = bz - az;
    const dd = dx * dx + dy * dy + dz * dz;
    const t = dd > 0
      ? Math.max(0, Math.min(1, -(ax * dx + ay * dy + az * dz) / dd))
      : 0;
    return Math.hypot(ax + t * dx, ay + t * dy, az + t * dz);
  }

  /** The ship's drawn position the last time `updateWarp()` ran the drop test,
   *  so the sweep reads the segment travelled since then rather than a point. */
  let warpSweepAtX = 0;
  let warpSweepAtY = 0;
  let warpSweepAtZ = 0;
  /** Where the dominant body stood at that same reading, and which body it was:
   *  the sweep is taken in ITS frame, so the segment's start end needs the
   *  centre it was measured against rather than this frame's. */
  let warpSweepBodyAtX = 0;
  let warpSweepBodyAtY = 0;
  let warpSweepBodyAtZ = 0;
  let warpSweepBodyId = '';
  let warpSweepSimS = 0;
  let warpSweepValid = false;
  /** What the last drop test read, for `--warp --park`'s own bar. */
  let warpSweepMinM: number | null = null;

  /**
   * The drop test's own reading: how close the ship came to the dominant body's
   * centre over this frame, in THAT body's frame.
   *
   * The frame is the whole point. At 1 DAY/S a frame carries Mars 3.4e7 m while
   * the ship's own Mars-relative step is 1.1e6 m, so a segment in heliocentric
   * metres is dominated by the body's travel and reads a crossing on a ship
   * parked at twice the envelope. `?warpsweephelio=1` restores that, measuring
   * the start end against the body's CURRENT centre instead of the one it stood
   * at when that end was taken.
   */
  function sweptDropMinM(env: WarpEnvelope, cx: number, cy: number, cz: number): number {
    const endX = cx - env.bodyAt.x;
    const endY = cy - env.bodyAt.y;
    const endZ = cz - env.bodyAt.z;
    if (!warpSweepValid) return Math.hypot(endX, endY, endZ);
    let wasX = env.bodyAt.x;
    let wasY = env.bodyAt.y;
    let wasZ = env.bodyAt.z;
    if (!warpSweepHelio()) {
      if (warpSweepBodyId === env.id) {
        wasX = warpSweepBodyAtX;
        wasY = warpSweepBodyAtY;
        wasZ = warpSweepBodyAtZ;
      } else {
        // Dominance changed inside the frame: the start end is re-expressed in
        // the NEW parent's frame, walked back over the frame's own seconds,
        // rather than the frame being skipped.
        const bv = deps.simBodies?.velocityAt(env.id) ?? BODY_WORLD_VELOCITY;
        const dtS = deps.flightSimSeconds - warpSweepSimS;
        wasX = env.bodyAt.x - bv.x * dtS;
        wasY = env.bodyAt.y - bv.y * dtS;
        wasZ = env.bodyAt.z - bv.z * dtS;
      }
    }
    return sweptMinDistance(warpSweepAtX - wasX, warpSweepAtY - wasY, warpSweepAtZ - wasZ,
      endX, endY, endZ);
  }

  /** This frame's own place for a body the pull set already carries, in metres. */
  function pullCentre(id: string): { x: number; y: number; z: number } | null {
    for (let i = 0; i < deps._pulls.length; i++) {
      const p = deps._pulls[i]!;
      if (p.gm > 0 && deps._pullIds[i] === id) return p.at;
    }
    return null;
  }

  /**
   * The same swept reading against the body the segment's START end was measured
   * in, for the frame where dominance changed under it.
   *
   * `sweptDropMinM` measures the whole segment in the frame of the body pulling
   * hardest at its END. A frame fast enough to cross a body's envelope is often
   * fast enough to leave that body's dominance in the same frame, and then the
   * crossing the ship actually made is read against the new parent - the Sun at
   * a few hundred million metres - and never against the body it went through.
   * The two ends name two bodies; both get swept.
   */
  function sweptDropPrevM(cx: number, cy: number, cz: number): {
    minM: number; captureRadiusM: number; id: string; name: string;
  } | null {
    if (!warpSweepValid || warpSweepBodyId === '') return null;
    const row = bodyById(deps.bodyRows, warpSweepBodyId);
    if (!row) return null;
    const now = pullCentre(warpSweepBodyId);
    if (now === null) return null;
    const helio = warpSweepHelio();
    const wasX = helio ? now.x : warpSweepBodyAtX;
    const wasY = helio ? now.y : warpSweepBodyAtY;
    const wasZ = helio ? now.z : warpSweepBodyAtZ;
    const minM = sweptMinDistance(warpSweepAtX - wasX, warpSweepAtY - wasY, warpSweepAtZ - wasZ,
      cx - now.x, cy - now.y, cz - now.z);
    return { minM, captureRadiusM: row.radius_m + bandEdges(row.radius_m).SYSTEM,
      id: row.id, name: row.display_name };
  }

  function showWarpNotice(text: string, capKey: string | null = null): void {
    warpNoticeText = text;
    warpNoticeKey = capKey;
    warpNoticeUntilS = performance.now() / 1000 + WARP_NOTICE_S;
  }

  /** Paint a notice with one letter drawn as a key cap, so it does not read as a digit. */
  function paintNotice(el: HTMLElement, text: string, key: string | null): void {
    const token = key === null ? '' : ` ${key} `;
    const at = key === null ? -1 : text.indexOf(token);
    if (at < 0) {
      el.textContent = text;
      return;
    }
    const cap = document.createElement('kbd');
    cap.className = 'hud-key';
    cap.textContent = key;
    el.replaceChildren(
      document.createTextNode(`${text.slice(0, at)} `), cap,
      document.createTextNode(text.slice(at + token.length - 1)));
  }

  /** Real time now, pick and all. One assignment: the drop is not a ramp. */
  function warpDrop(reason: string): void {
    if (warpStick()) return;
    warpGoal = null;
    const gc = gameClock();
    const already = gc.rate === REAL_TIME_RATE && gc.warpTarget === REAL_TIME_RATE
      && warpPick() === 0;
    gc.dropToRealTime();
    setWarpLevel(0);
    if (already) return;
    showWarpNotice(`WARP OFF: ${reason}`);
  }

  /** Above the ceiling: one step down to it, not off. */
  function warpStepDown(): void {
    const idx = warpCeilingIdx();
    if (idx === 0) { warpDrop('THRUST'); return; }
    if (warpStick()) return;
    const level = WARP_LEVELS[idx];
    if (!level) return;
    setWarpLevel(idx);
    gameClock().setRate(level.rate);
    showWarpNotice(`WARP DOWN: ${level.label}`);
  }

  /**
   * The player's own pick. The rate eases onto it; nothing here jumps.
   *
   * Reaching for the TOP level while the throttle is held does nothing: a frame
   * of full throttle there is 4,236 m/s, and refusing the pick is quieter than
   * taking it and stepping it down on the same frame. Every level under it is
   * the player's to hold mid-burn.
   */
  function setWarpPick(idx: number, pilot = true): void {
    const clamped = Math.max(0, Math.min(WARP_LEVELS.length - 1, Math.round(idx)));
    // A pick made by hand while the delta-v map's autopilot flies asks the CLOCK
    // for a rate and nothing else: the stick is what hands a trip back, and a
    // player who asks the clock to hurry has not asked to fly. The ask is held
    // and answered on the next frame, since the autopilot asks for its own rate
    // through this same call with `pilot` false and would write over it here.
    if (pilot && deps.autopilot !== null && !AUTOPILOT_RATE_CANCELS) {
      autoWarp?.standDown();
      deps.autopilotWarpWish = WARP_LEVELS[clamped]?.rate ?? REAL_TIME_RATE;
      const cmd = deps.autopilotCmd;
      requestAutopilotWarp(cmd?.warpRate ?? deps.autopilotWarpWish,
        cmd?.warpCeiling ?? deps.autopilotWarpWish);
      return;
    }
    // A pick made by hand takes the stick back off the delta-v map's autopilot,
    // and the clock back off a run toward a goal.
    if (pilot) { notePilotInput(); warpGoal = null; }
    if (warpPinned) return;
    const level = WARP_LEVELS[clamped];
    if (!level) return;
    if (level.rate !== REAL_TIME_RATE && deps.engagement !== null) return;
    if (clamped > warpCeilingIdx()) return;
    if (pilot) track('warp_set', { level: level.short, from_level: WARP_LEVELS[warpPick()]?.short ?? 'REAL' });
    setWarpLevel(clamped);
    warpNoticeText = null;
    gameClock().setWarpTarget(level.rate);
  }

  /**
   * The pick the state carries, pushed onto the clock.
   *
   * `load()` swaps the state, which already moves the pick; the rate the clock
   * holds is a second copy and has to be told. None of the refusals in
   * `setWarpPick` apply: the state is the pick, not a request to change it.
   *
   * A pin outranks it. `initialState()` carries pick 0, so a scenario load
   * would otherwise hand the clock back to real time while `warpPinned` still
   * reads true, leaving a `?flightwarp=120` run stepping at 1x.
   */
  function adoptPick(): void {
    warpGoal = null;
    // A load or a reset swaps the ship under the trip; Auto-warp does not carry on over it.
    autoWarp?.standDown();
    if (warpPinned) { paintWarp(); return; }
    const level = WARP_LEVELS[warpPick()] ?? WARP_LEVELS[0]!;
    gameClock().setWarpTarget(level.rate);
    warpNoticeText = null;
    paintWarp();
  }

  /**
   * The autopilot's own pace: any rate, not only a level, up to the ceiling. It
   * lands as given, since its caller eases it; the pick follows as the highest
   * level under it, so the buttons still say which rung the clock is past.
   */
  function setAutopilotRate(rate: number): void {
    if (warpPinned || deps.engagement !== null || !Number.isFinite(rate)) return;
    const ceilingIdx = warpCeilingIdx();
    const r = Math.max(REAL_TIME_RATE, Math.min(rate, WARP_LEVELS[ceilingIdx]?.rate ?? REAL_TIME_RATE));
    let idx = 0;
    for (let i = 1; i <= ceilingIdx; i++) {
      if ((WARP_LEVELS[i]?.rate ?? Infinity) <= r * (1 + 1e-9)) idx = i;
    }
    if (idx !== warpPick()) setWarpLevel(idx);
    gameClock().setRate(r);
  }

  function readoutRate(): number {
    return warpReadoutNominal() ? gameClock().rate : measuredRate;
  }

  /** Simulated seconds a real second over the last RATE_WINDOW_MS, off the samples. */
  function sampleRate(nowMs: number): void {
    rateSamplesMs.push(nowMs);
    rateSamplesT.push(gameClock().t);
    while (rateSamplesMs.length > 2 && (nowMs - rateSamplesMs[1]! >= RATE_WINDOW_MS || rateSamplesMs.length > 256)) {
      rateSamplesMs.shift();
      rateSamplesT.shift();
    }
    const spanMs = nowMs - rateSamplesMs[0]!;
    if (spanMs > 0) measuredRate = ((gameClock().t - rateSamplesT[0]!) * 1000) / spanMs;
  }

  /**
   * The trip's own line for the warp control, or null where nothing is holding
   * the clock under what the player asked for.
   *
   * A pick can be taken and not given: a burn is re-aimed and re-integrated every
   * `burnStepS` simulated seconds and the clock gets what a frame of those steps
   * buys, a rung under the ladder's top; a wait or a coast stops short of the
   * next dated step. A button that answers a press with nothing reads as a broken
   * button, so the control says which rate it took and why while it holds it.
   */
  function warpHeldByAutopilot(): string | null {
    const cmd = deps.autopilotCmd;
    if (deps.autopilot === null || cmd === null || deps.autopilotWarpWish === null) return null;
    if (!(deps.autopilotWarpWish > cmd.warpCeiling + 1e-6)) return null;
    const rate = cmd.warpCeiling;
    const label = WARP_LEVELS.find((l) => l.rate === rate)?.label ?? `${Math.round(rate)}x`;
    return `${cmd.burnStepS > 0 ? 'THE BURN' : 'THE TRIP'} HOLDS THE CLOCK AT ${label}`;
  }

  /**
   * Why the heat is holding the clock at real time: the ladder, or the player
   * pushing past the red line, whose damage a warped frame would land by the
   * hundred. A pinned rate and a fight are their own holds and keep the
   * question to themselves.
   */
  function heatHoldLine(): string | null {
    if (warpPinned || deps.engagement !== null) return null;
    if (vitals.redLine) return RED_LINE_HOLD_LINE;
    return heatHoldsClock(ship.heat, vitals.heatStepInput()) ? HEAT_HOLD_LINE : null;
  }

  function heatHoldsWarp(): boolean {
    return heatHoldLine() !== null;
  }

  /** The control's own face: the picked level, whether it can be reached, the notice. */
  function paintWarp(): void {
    const nowS = performance.now() / 1000;
    const burnHold = warpHeldByAutopilot();
    const heatHold = heatHoldLine();
    const hold = heatHold ?? burnHold ?? autoWarp?.line() ?? warpGoal?.runningLine ?? null;
    // A transient notice (WARP OFF, WARP DOWN, WARP BACK) is the urgent one and
    // wins for its own lifetime; the hold line is the steady state underneath it
    // and returns once the transient expires.
    const transientLive = warpNoticeText !== null && nowS < warpNoticeUntilS;
    const noticeOn = hold !== null || transientLive;
    const noticeText = transientLive ? warpNoticeText! : hold ?? '';
    const noticeKey = transientLive ? warpNoticeKey : null;
    const locked = warpPinned || warpCeilingIdx() === 0;
    const pinnedRate = warpPinned ? Math.round(gameClock().rate) : 0;
    // While GO flies, the face is the rate the clock really moves at: Auto-warp
    // and WARP TO NEXT both run it between and past the levels.
    const goRate = deps.autopilot !== null && !warpPinned
      ? clockRateText(readoutRate()).toUpperCase().replace(/X$/, '×') : '';
    const key = `${warpPick()}|${locked ? 1 : 0}|${pinnedRate}|${goRate}`
      + `|${noticeOn ? noticeText : ''}|${noticeOn ? noticeKey ?? '' : ''}`;
    if (key === warpPaintKey) return;
    warpPaintKey = key;
    const level = WARP_LEVELS[warpPick()] ?? WARP_LEVELS[0]!;
    if (elWarpLevel) elWarpLevel.textContent = warpPinned ? `${pinnedRate}\u00d7` : goRate !== '' ? goRate : level.label;
    if (elWarpBar) elWarpBar.classList.toggle('warp-locked', locked);
    document.querySelectorAll<HTMLButtonElement>('.warp-btn').forEach((btn) => {
      const v = Number(btn.dataset['value'] ?? '1');
      btn.classList.toggle('active', !warpPinned && v === level.rate);
    });
    if (elWarpNotice) {
      elWarpNotice.classList.toggle('hidden', !noticeOn);
      if (noticeOn) paintNotice(elWarpNotice, noticeText, noticeKey);
    }
  }

  function runTo(goal: WarpGoal): string | null {
    if (warpPinned) return 'PINNED';
    if (deps.engagement !== null) return 'FIGHT';
    if (deps.autopilot !== null) return 'THE TRIP HOLDS THE CLOCK';
    const heat = heatHoldLine();
    if (heat !== null) return heat;
    if (warpCeilingIdx() === 0) return warpReason(1) ?? 'LOCKED';
    warpGoal = goal;
    warpNoticeText = null;
    return null;
  }

  /** The highest level whose frame at the recent peak frame time stays within the goal's remaining seconds or slack; down lands at once, up eases. */
  function runGoal(goal: WarpGoal): void {
    const gc = gameClock();
    if (goal.reached()) {
      warpGoal = null;
      gc.dropToRealTime();
      setWarpLevel(0);
      showWarpNotice(goal.arrivedLine);
      return;
    }
    const remS = goal.remainingS();
    if (remS === null) { warpDrop(`${goal.arrivedLine} LOST`); return; }
    const room = Math.max(remS, goal.slackS());
    const frameS = Math.min(0.1, warpFramePeakS * 1.25);
    let idx = 0;
    for (let i = warpCeilingIdx(); i > 0; i--) {
      if ((WARP_LEVELS[i]?.rate ?? Infinity) * frameS <= room) { idx = i; break; }
    }
    const level = WARP_LEVELS[idx] ?? WARP_LEVELS[0]!;
    if (idx !== warpPick()) setWarpLevel(idx);
    if (level.rate < gc.rate) gc.setRate(level.rate);
    else if (gc.warpTarget !== level.rate) gc.setWarpTarget(level.rate);
  }

  /**
   * One tick of the policy, run before the clock advances so a drop lands on the
   * simulated seconds this frame is about to take rather than the next frame's.
   *
   * The envelope drop is EDGE triggered - the crossing inward, not the state of
   * being near. A ship parked in orbit waiting for a launch window is entitled
   * to warp; a ship arriving at 86,400:1 is not.
   */
  function updateWarp(): void {
    if (analyticsPerFrame()) track('ui_open', { ui: 'per-frame', action: 'open' });
    const nowMs = performance.now();
    if (warpLastUpdateMs > 0) {
      warpFramePeakS = Math.max(Math.min(0.1, (nowMs - warpLastUpdateMs) / 1000), warpFramePeakS * 0.9);
    }
    warpLastUpdateMs = nowMs;
    sampleRate(nowMs);
    const fightOn = deps.engagement !== null;
    const fightStarted = fightOn && !warpFightWas;
    const fightEnded = !fightOn && warpFightWas;
    warpFightWas = fightOn;
    if (!warpPinned) {
      if (fightStarted) {
        // The clock itself already dropped to real time in enterEngagement();
        // this is the pick-preserving notice round 15's own FIGHT drop is not.
        if (!CLOCK_CONST) showWarpNotice('FIGHT: REAL TIME');
      } else if (!fightOn) {
        const env = warpEnvelope();
        const curX = simPos.world.x;
        const curY = simPos.world.y;
        const curZ = simPos.world.z;
        const alreadyFlagged = env !== null && warpEnvelopeInside && warpEnvelopeBody === env.id;
        const sweptMinM = env === null ? null : sweptDropMinM(env, curX, curY, curZ);
        const prev = warpDropPoint() || (env !== null && warpSweepBodyId === env.id)
          ? null : sweptDropPrevM(curX, curY, curZ);
        const prevFlagged = prev !== null && warpEnvelopeInside && warpEnvelopeBody === prev.id;
        const prevCrossed = prev !== null && !prevFlagged && prev.minM <= prev.captureRadiusM;
        // `?warpdroppoint=1` is this test's own control: it drops back to
        // reading `env.inside` alone, the pre-fix point test.
        const crossedIn = env !== null && !alreadyFlagged && (warpDropPoint() ? env.inside
          : sweptMinM !== null && sweptMinM <= env.captureRadiusM);
        // A frame that crosses both capture spheres used to drop on whichever
        // the end sweep caught, whatever the start body's own read was.
        const endCrossing: WarpDropCrossing | null = crossedIn && env !== null && sweptMinM !== null
          ? { id: env.id, name: env.name, minM: sweptMinM } : null;
        const prevCrossing: WarpDropCrossing | null = prevCrossed && prev !== null
          ? { id: prev.id, name: prev.name, minM: prev.minM } : null;
        const drop = nearerWarpDrop(endCrossing, prevCrossing, warpDropEndFirst());
        warpSweepMinM = drop !== null ? drop.minM : sweptMinM;
        warpEnvelopeBody = env === null ? '' : env.id;
        warpEnvelopeInside = env !== null && env.inside;
        warpSweepAtX = curX;
        warpSweepAtY = curY;
        warpSweepAtZ = curZ;
        warpSweepBodyId = env === null ? '' : env.id;
        warpSweepBodyAtX = env?.bodyAt.x ?? 0;
        warpSweepBodyAtY = env?.bodyAt.y ?? 0;
        warpSweepBodyAtZ = env?.bodyAt.z ?? 0;
        warpSweepSimS = deps.flightSimSeconds;
        warpSweepValid = true;
        if (drop !== null && !autoWarpDriving()) warpDrop(`NEAR ${drop.name.toUpperCase()}`);
        else if (warpPick() > warpCeilingIdx()) warpStepDown();
        else if (fightEnded && !CLOCK_CONST && warpPick() !== 0) {
          const level = WARP_LEVELS[warpPick()] ?? WARP_LEVELS[0]!;
          showWarpNotice(`WARP BACK: ${level.label}`);
        }
      }
    }
    if (warpGoal !== null) {
      if (warpPinned || fightOn || deps.autopilot !== null) warpGoal = null;
      else runGoal(warpGoal);
    }
    // A fight holds the clock itself and the release has to stay out of its way,
    // or it would hand it back.
    if (deps.engagement === null) gameClock().holdRealTime(heatHoldsWarp());
    paintWarp();
  }

  document.querySelectorAll<HTMLButtonElement>('.warp-btn').forEach((btn) => {
    btn.addEventListener('click', () => {
      const v = Number(btn.dataset['value'] ?? '1');
      const idx = WARP_LEVELS.findIndex((l) => l.rate === v);
      setWarpPick(idx < 0 ? 0 : idx);
      paintWarp();
    });
  });
  // [ and ] step the pick down and up, the keys the scrub used to cycle on.
  input.on('simSpeedDown', () => { setWarpPick(warpPick() - 1); paintWarp(); });
  input.on('simSpeedUp', () => { setWarpPick(warpPick() + 1); paintWarp(); });
  // 1/2/3/4 pick a level outright - the same call and the same refusals as
  // clicking its button, never a second path.
  input.on('warpLevel1', () => { setWarpPick(0); paintWarp(); });
  input.on('warpLevel2', () => { setWarpPick(1); paintWarp(); });
  input.on('warpLevel3', () => { setWarpPick(2); paintWarp(); });
  input.on('warpLevel4', () => { setWarpPick(3); paintWarp(); });

  {
    // Parsed fresh rather than through urlParam: warp-pin-check.mjs changes the query between legs.
    const raw = TEST_SWITCHES ? new URLSearchParams(location.search).get('flightwarp') : null;
    const n = raw === null ? Number.NaN : Number(raw);
    if (Number.isFinite(n) && n > 0) {
      warpPinned = true;
      gameClock().setRate(n);
    }
  }
  paintWarp();

  const readout = {
    get levelText(): string | null { return elWarpLevel?.textContent ?? null; },
    get noticeText(): string | null {
      return elWarpNotice && !elWarpNotice.classList.contains('hidden')
        ? elWarpNotice.textContent : null;
    },
  };

  return {
    readout,
    get pinned(): boolean { return warpPinned; },
    set pinned(on: boolean) { warpPinned = on; },
    get pickIdx(): number { return warpPick(); },
    get noticeText(): string | null { return warpNoticeText; },
    get noticeUntilS(): number { return warpNoticeUntilS; },
    get sweepMinM(): number | null { return warpSweepMinM; },
    invalidateSweep(): void { warpSweepValid = false; },
    underControl: warpUnderControl,
    envelope: warpEnvelope,
    ceilingIdx: warpCeilingIdx,
    reachable: warpReachable,
    reason: warpReason,
    heatHolds: heatHoldsWarp,
    heatHoldLine,
    showNotice: showWarpNotice,
    drop: warpDrop,
    setPick: setWarpPick,
    adoptPick,
    runTo,
    setAutopilotRate,
    bindAutoWarp(hooks: AutoWarpHooks): void { autoWarp = hooks; },
    get autoWarpDriving(): boolean { return autoWarpDriving(); },
    get measuredRate(): number { return readoutRate(); },
    get goal(): WarpGoal | null { return warpGoal; },
    paint: paintWarp,
    update: updateWarp,
    get parkRadialBasis(): boolean { return parkRadialBasis; },
    setParkRadialBasis(on: boolean): void { parkRadialBasis = on === true; },
    get skipTargetT(): number | null { return warpSkipTargetT; },
    get skipMs(): number { return warpSkipMs; },
    get skipTicks(): number { return warpSkipTicks; },
    get skipping(): boolean { return warpSkipTargetT !== null; },
    startSkip(targetT: number): void {
      warpSkipTargetT = targetT;
      warpSkipMs = 0;
      warpSkipTicks = 0;
    },
    clearSkip(): void { warpSkipTargetT = null; },
    addSkipTick(ms: number): void {
      warpSkipTicks += 1;
      warpSkipMs += ms;
    },
  };
}
